import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { activityLogService } from '@/services/activityLogService';
import { setUserEmploymentStatusWithEncompass } from '@/services/encompassUserLifecycleService';
import { notificationService } from '@/services/notificationService';
import { useAuth } from '@/contexts/useAuth';
import { checkDatabaseFunctions } from '@/utils/checkDatabaseFunctions';
import { createComponentLogger } from '@/utils/logger';
import { supabase } from '@/utils/supabase';
import type { User } from './types';
import { filterUsers } from './utils';
import {
  UserManagementView,
  type UserManagementSortConfig,
  type UserManagementTab
} from './UserManagementView';
import {
  applyUserManagementFilters,
  buildUserManagementFilterOptions,
  clearUserManagementFilter,
  EMPTY_USER_MANAGEMENT_FILTERS,
  toggleUserManagementFilter,
  type UserManagementFilterFacet,
  type UserManagementFilters,
  type UserManagementStateMatchMode
} from './userManagementFilters';

const log = createComponentLogger('UserManagement', 'user-management');

const PARTNER_MANAGEABLE_ROLES = new Set([
  'loan_officer',
  'loan_officer_assistant',
  'processor',
  'underwriter',
  'manager',
  'closer',
  'compliance_specialist'
]);

const ROLE_LABELS: Record<string, string> = {
  loan_officer: 'Loan Officer',
  loan_officer_assistant: 'Loan Officer Assistant',
  processor: 'Processing',
  underwriter: 'Underwriter',
  manager: 'Manager',
  admin: 'Admin',
  closer: 'Closer',
  compliance_specialist: 'Compliance Specialist',
  partner: 'Partner',
  recruiter: 'Recruiter'
};

const normalizeRoles = (roles: unknown): string[] =>
  Array.isArray(roles)
    ? roles
        .filter((role): role is string => typeof role === 'string')
        .map((role) => role.trim().toLowerCase())
        .filter(Boolean)
    : [];

const formatRole = (role: string): string => {
  if (!role) {
    return 'No Role Assigned';
  }
  return ROLE_LABELS[role] || `Unknown Role (${role})`;
};

interface PartnerManageableProfile {
  roles?: unknown;
  organization_id?: string | null;
  deleted_at?: string | null;
  employment_status?: string | null;
}

interface RawUserProfile extends PartnerManageableProfile {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  mobile_phone?: string | null;
  title?: string | null;
  office_extension?: string | null;
  years_of_experience?: number | null;
  mortgage_industry_start_year?: number | null;
  roles?: string[] | null;
  nmls_number?: string | null;
  license_states?: string[] | null;
  profile_photo_facial?: string | null;
  profile_photo_body?: string | null;
  profile_photo?: string | null;
  profile_picture_url?: string | null;
  bio?: string | null;
  specialties?: string[] | null;
  website?: string | null;
  linkedin_url?: string | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  tiktok_url?: string | null;
  google_business_review_url?: string | null;
  zillow_reviews_url?: string | null;
  calendar_link?: string | null;
  mortgage_specialties?: string[] | null;
  website_slug?: string | null;
  website_template?: string | null;
  website_enabled?: boolean | null;
  created_at: string;
  encompass_user_id?: string | null;
  encompass_sync_status?: string | null;
  encompass_synced_at?: string | null;
  optimal_blue_user_id?: string | null;
  optimal_blue_username?: string | null;
  optimal_blue_linked_at?: string | null;
  optimal_blue_business_channel_id?: string | null;
  optimal_blue_current_schedule_id?: number | null;
  optimal_blue_current_schedule_name?: string | null;
  optimal_blue_current_schedule_scope?: string | null;
}

const isPartnerManageableProfile = (
  profile: PartnerManageableProfile,
  currentOrganizationId?: string | null
): boolean => {
  const roles = normalizeRoles(profile.roles);
  return Boolean(
    currentOrganizationId &&
    profile.organization_id === currentOrganizationId &&
    (profile.deleted_at === null || profile.deleted_at === undefined) &&
    (profile.employment_status === 'active' || profile.employment_status === 'terminated') &&
    roles.length > 0 &&
    roles.every((role) => PARTNER_MANAGEABLE_ROLES.has(role))
  );
};

const sortUsers = (
  users: User[],
  key: string,
  direction: Exclude<UserManagementSortConfig['direction'], null>
): User[] => [...users].sort((a, b) => {
  let aValue: string | number;
  let bValue: string | number;

  switch (key) {
    case 'name':
      aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
      bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
      break;
    case 'email':
      aValue = a.email.toLowerCase();
      bValue = b.email.toLowerCase();
      break;
    case 'role':
      aValue = a.roles?.[0] || '';
      bValue = b.roles?.[0] || '';
      break;
    case 'nmls':
      aValue = a.nmls || '';
      bValue = b.nmls || '';
      break;
    case 'states':
      aValue = a.licensedStates.length;
      bValue = b.licensedStates.length;
      break;
    case 'status':
      aValue = a.status || '';
      bValue = b.status || '';
      break;
    default:
      return 0;
  }

  if (aValue < bValue) {
    return direction === 'asc' ? -1 : 1;
  }
  if (aValue > bValue) {
    return direction === 'asc' ? 1 : -1;
  }
  return 0;
});

const toUser = (profile: RawUserProfile): User => {
  const firstName = profile.first_name?.trim() || '';
  const lastName = profile.last_name?.trim() || '';
  const phoneValue = profile.phone?.trim() || '';
  const phone = phoneValue === '0000000000' ? '' : phoneValue;

  return {
    id: profile.id,
    firstName,
    lastName,
    email: profile.email,
    phone,
    mobilePhone: profile.mobile_phone?.trim() || '',
    title: profile.title || '',
    officeExtension: profile.office_extension || '',
    yearsOfExperience: profile.years_of_experience || undefined,
    mortgageIndustryStartYear: profile.mortgage_industry_start_year || undefined,
    role: formatRole(profile.roles?.[0] || 'user'),
    roles: profile.roles?.map(formatRole) || [],
    address: {
      type: 'corporate',
      value: '123 Corporate Blvd'
    },
    nmls: profile.nmls_number || '',
    licensedStates: profile.license_states || [],
    profilePicture: profile.profile_photo_facial ||
      profile.profile_photo_body ||
      profile.profile_photo ||
      profile.profile_picture_url || '',
    logo: '',
    bio: profile.bio || '',
    specialties: profile.specialties || [],
    socialMedia: {
      website: profile.website || '',
      linkedin: profile.linkedin_url || '',
      facebook: profile.facebook_url || '',
      instagram: profile.instagram_url || '',
      tiktok: profile.tiktok_url || '',
      googleBusinessReview: profile.google_business_review_url || '',
      zillowReviews: profile.zillow_reviews_url || ''
    },
    calendarLink: profile.calendar_link || '',
    status: profile.employment_status === 'active'
      ? 'Active'
      : profile.employment_status === 'terminated'
        ? 'Terminated'
        : 'Pending',
    hasSpecificBrand: false,
    brandName: '',
    mortgageSpecialties: profile.mortgage_specialties || [],
    websiteSlug: profile.website_slug || undefined,
    websiteTemplate: profile.website_template || undefined,
    websiteEnabled: Boolean(profile.website_enabled),
    createdAt: profile.created_at,
    encompassUserId: profile.encompass_user_id || undefined,
    encompassSyncStatus: profile.encompass_sync_status || undefined,
    encompassSyncedAt: profile.encompass_synced_at || undefined,
    optimalBlueUserId: profile.optimal_blue_user_id || undefined,
    optimalBlueUsername: profile.optimal_blue_username || undefined,
    optimalBlueLinkedAt: profile.optimal_blue_linked_at || undefined,
    optimalBlueBusinessChannelId: profile.optimal_blue_business_channel_id ?? null,
    optimalBlueCurrentScheduleId: profile.optimal_blue_current_schedule_id ?? null,
    optimalBlueCurrentScheduleName: profile.optimal_blue_current_schedule_name ?? null,
    optimalBlueCurrentScheduleScope: profile.optimal_blue_current_schedule_scope ?? null
  };
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<UserManagementTab>('active');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<UserManagementSortConfig>({ key: '', direction: null });
  const [filters, setFilters] = useState<UserManagementFilters>(() => ({
    roles: [],
    states: [],
    integrations: []
  }));
  const [stateMatchMode, setStateMatchMode] =
    useState<UserManagementStateMatchMode>('any');
  const isPartnerMode = isPartner && !isAdmin;

  const checkAdminRole = async () => {
    if (!currentUser) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from('users')
        .select('roles, organization_id')
        .eq('id', currentUser.id)
        .single();

      if (!error && data) {
        const roles = normalizeRoles(data.roles);
        setIsAdmin(roles.includes('admin'));
        setIsPartner(roles.includes('partner'));
      }
    } catch (error) {
      log.error('Failed to check admin role', { userId: currentUser.id }, error as Error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const { data: { session } } = await supabase.auth.getSession();
      const { data: currentProfile } = await supabase
        .from('users')
        .select('roles, organization_id')
        .eq('id', session?.user?.id)
        .single();

      const currentRoles = normalizeRoles(currentProfile?.roles);
      const partnerMode = currentRoles.includes('partner') && !currentRoles.includes('admin');
      const currentOrganizationId = currentProfile?.organization_id ?? null;
      const initialQuery = await supabase
        .from('users')
        .select('*')
        .order('first_name', { ascending: true });
      const { error } = initialQuery;
      let profiles = initialQuery.data;

      if ((!profiles || profiles.length === 0) && !partnerMode) {
        const { data: currentUserProfile } = await supabase
          .from('users')
          .select('*')
          .eq('id', session?.user?.id)
          .single();
        if (currentUserProfile) {
          profiles = [currentUserProfile];
        }
      }

      if (error) {
        log.error('Failed to load users', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        toast.error('Failed to load users');
        setLoadError('The user directory request failed. Check your connection and try again.');
        return;
      }

      const visibleProfiles = partnerMode
        ? (profiles || []).filter((profile) => isPartnerManageableProfile(profile, currentOrganizationId))
        : (profiles || []);
      setUsers(visibleProfiles.map(toUser));
    } catch (error) {
      log.error('Error in loadUsers', {}, error as Error);
      toast.error('Failed to load users');
      setLoadError('The user directory request failed. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
    void checkAdminRole();

    if (process.env.NODE_ENV === 'development') {
      void checkDatabaseFunctions().then((result) => {
        log.debug('Database functions check', { metadata: result });
      });
    }
    // The screen reloads when the authenticated actor changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (isPartnerMode && activeTab === 'pending') {
      setActiveTab('active');
    }
  }, [activeTab, isPartnerMode]);

  const usersForActiveTab = useMemo(() => {
    if (activeTab === 'active') {
      return users.filter((user) => user.status === 'Active');
    }
    if (activeTab === 'pending') {
      return users.filter((user) => user.status === 'Pending');
    }
    return users.filter((user) => user.status === 'Terminated');
  }, [activeTab, users]);

  const searchedUsers = useMemo(
    () => filterUsers(usersForActiveTab, searchTerm),
    [searchTerm, usersForActiveTab]
  );

  const filterOptions = useMemo(
    () => buildUserManagementFilterOptions(searchedUsers),
    [searchedUsers]
  );

  const visibleUsers = useMemo(() => {
    const filtered = applyUserManagementFilters(
      searchedUsers,
      filters,
      stateMatchMode
    );

    if (sortConfig.key && sortConfig.direction) {
      return sortUsers(filtered, sortConfig.key, sortConfig.direction);
    }
    return filtered;
  }, [filters, searchedUsers, sortConfig, stateMatchMode]);

  const counts = useMemo(() => ({
    active: users.filter((user) => user.status === 'Active').length,
    pending: users.filter((user) => user.status === 'Pending').length,
    deactivated: users.filter((user) => user.status === 'Terminated' || !user.status).length
  }), [users]);

  const handleSort = (key: string) => {
    let direction: UserManagementSortConfig['direction'] = 'asc';
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc'
        ? 'desc'
        : sortConfig.direction === 'desc'
          ? null
          : 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterToggle = (facet: UserManagementFilterFacet, value: string) => {
    setFilters((current) => toggleUserManagementFilter(current, facet, value));
  };

  const handleFilterClear = (facet: UserManagementFilterFacet) => {
    setFilters((current) => clearUserManagementFilter(current, facet));
    if (facet === 'states') {
      setStateMatchMode('any');
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      roles: [...EMPTY_USER_MANAGEMENT_FILTERS.roles],
      states: [...EMPTY_USER_MANAGEMENT_FILTERS.states],
      integrations: [...EMPTY_USER_MANAGEMENT_FILTERS.integrations]
    });
    setStateMatchMode('any');
  };

  const handleEditUser = (user: User) => {
    navigate(`/internal?section=edit-user&userId=${user.id}`);
  };

  const confirmDeactivate = async (userId: string) => {
    try {
      const deactivatedUser = users.find((user) => user.id === userId);
      if (deactivatedUser?.encompassUserId) {
        try {
          const statusResult = await setUserEmploymentStatusWithEncompass(userId, 'terminated');
          if (statusResult.warning) {
            toast.warning(`User will be deactivated, but Encompass access was not fully updated: ${statusResult.warning}`);
          }
        } catch (statusError) {
          log.error('Failed to disable Encompass before deactivation; aborting', { userId }, statusError as Error);
          toast.error(statusError instanceof Error
            ? statusError.message
            : 'Failed to disable Encompass account. User was NOT deactivated.');
          return false;
        }
      }

      const { data, error } = await supabase.rpc('deactivate_user', {
        p_user_id: userId,
        p_reason: 'Deactivated by admin'
      });
      if (error) {
        log.error('Failed to deactivate user', {
          userId,
          code: error.code,
          message: error.message,
          details: error.details
        }, error as Error);
        toast.error(error.code === '42883'
          ? 'Deactivate function not found. Please apply database migrations.'
          : error.message || 'Failed to deactivate user');
        return false;
      }
      if (!data || !data.success) {
        toast.error(data?.error || 'Unknown error occurred');
        return false;
      }
      toast.success('User deactivated successfully');
      await loadUsers();
      return true;
    } catch (error) {
      log.error('Error in confirmDeactivate', { userId }, error as Error);
      toast.error('Failed to deactivate user');
      return false;
    }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      const user = users.find((candidate) => candidate.id === userId);
      if (!user) {
        return false;
      }
      if (user.status === 'Pending') {
        toast.error('Pending users must be approved through the approval workflow');
        return false;
      }

      const newEmploymentStatus = user.status === 'Active' ? 'terminated' : 'active';
      const statusResult = await setUserEmploymentStatusWithEncompass(userId, newEmploymentStatus);
      await activityLogService.logUserChange(
        userId,
        'status_changed',
        [{
          field: 'employment_status',
          oldValue: user.status === 'Active' ? 'active' : 'terminated',
          newValue: newEmploymentStatus
        }]
      );
      if (statusResult.warning) {
        toast.warning(`User status updated, but Encompass access was not updated: ${statusResult.warning}`);
      }
      toast.success(`User ${newEmploymentStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      await loadUsers();
      return true;
    } catch (error) {
      log.error('Error in handleToggleStatus', { userId }, error as Error);
      toast.error('Failed to update user status');
      return false;
    }
  };

  const handleApproveUser = async (userToApprove: User, selectedRole: string) => {
    try {
      const { data: adminProfile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', currentUser?.id)
        .single();
      const organizationId = adminProfile?.organization_id || null;
      if (!organizationId) {
        log.error('Admin has no organization', { adminId: currentUser?.id });
        toast.error('Admin profile has no organization assigned');
        return false;
      }

      const { error } = await supabase
        .from('users')
        .update({
          roles: [selectedRole],
          organization_id: organizationId,
          updated_at: new Date().toISOString()
        })
        .eq('id', userToApprove.id);
      if (error) {
        log.error('Failed to approve user', { userId: userToApprove.id }, error as Error);
        toast.error('Failed to approve user');
        return false;
      }

      const statusResult = await setUserEmploymentStatusWithEncompass(userToApprove.id, 'active');
      if (statusResult.warning) {
        toast.warning(`User approved, but Encompass access was not updated: ${statusResult.warning}`);
      }
      await activityLogService.logUserChange(
        userToApprove.id,
        'status_changed',
        [
          { field: 'roles', oldValue: null, newValue: selectedRole },
          { field: 'employment_status', oldValue: 'pending', newValue: 'active' }
        ],
        { action: 'user_approved' }
      );
      const emailResult = await notificationService.sendUserApprovedEmail(
        userToApprove.id,
        selectedRole,
        userToApprove.firstName
      );
      if (emailResult.success) {
        log.info('Approval email sent successfully', { userId: userToApprove.id, emailId: emailResult.emailId });
      } else {
        log.warn('Failed to send approval email', { userId: userToApprove.id, error: emailResult.error });
      }
      toast.success(`${userToApprove.firstName} ${userToApprove.lastName} approved as ${selectedRole}`);
      await loadUsers();
      return true;
    } catch (error) {
      log.error('Error in handleApproveUser', { userId: userToApprove.id }, error as Error);
      toast.error('Failed to approve user');
      return false;
    }
  };

  const handleRejectUser = async (userId: string) => {
    try {
      const statusResult = await setUserEmploymentStatusWithEncompass(userId, 'terminated');
      if (statusResult.warning) {
        toast.warning(`User rejected, but Encompass access was not updated: ${statusResult.warning}`);
      }
      const { error } = await supabase
        .from('users')
        .update({ roles: null, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (error) {
        log.error('Failed to reject user', { userId }, error as Error);
        toast.error('Failed to reject user');
        return false;
      }
      await activityLogService.logUserChange(
        userId,
        'status_changed',
        [
          { field: 'employment_status', oldValue: 'pending', newValue: 'terminated' },
          { field: 'roles', oldValue: null, newValue: null }
        ],
        { action: 'user_rejected' }
      );
      toast.success('User registration rejected');
      await loadUsers();
      return true;
    } catch (error) {
      log.error('Error in handleRejectUser', { userId }, error as Error);
      toast.error('Failed to reject user');
      return false;
    }
  };

  return (
    <UserManagementView
      users={users}
      visibleUsers={visibleUsers}
      counts={counts}
      activeTab={activeTab}
      onActiveTabChange={setActiveTab}
      searchTerm={searchTerm}
      onSearchTermChange={setSearchTerm}
      filters={filters}
      stateMatchMode={stateMatchMode}
      onStateMatchModeChange={setStateMatchMode}
      filterOptions={filterOptions}
      onFilterToggle={handleFilterToggle}
      onFilterClear={handleFilterClear}
      onClearAllFilters={handleClearAllFilters}
      sortConfig={sortConfig}
      onSort={handleSort}
      loading={loading}
      loadError={loadError}
      onRetry={() => void loadUsers()}
      isAdmin={isAdmin}
      isPartnerMode={isPartnerMode}
      onEdit={handleEditUser}
      onDeactivate={(user) => isAdmin
        ? confirmDeactivate(user.id)
        : handleToggleStatus(user.id)}
      onReactivate={(user) => handleToggleStatus(user.id)}
      onApprove={handleApproveUser}
      onReject={(user) => handleRejectUser(user.id)}
    />
  );
};

export default UserManagement;
